import type { UserListItem } from '@/types';

import React from 'react';
import { RiEdit2Line, RiBuildingLine, RiUserSettingsLine, RiNotificationLine, RiDownloadLine } from '@remixicon/react';
import { Button, Chip, Tooltip, Avatar, AvatarGroup, Popover, PopoverTrigger, PopoverContent } from '@heroui/react';
import { useAtom, useAtomValue } from 'jotai';

import { TablePage, TableFilters, ColumnsToggleDropdown, FilterSelect } from '@/components/table';
import { usersListParamsAtom, usersQueryAtom } from '@/store';
import { useTableExport } from '@/components/table/useTableExport';
import { ExportConfirmModal } from '@/components/table/ExportConfirmModal';

// Custom hooks
import { useUsersFilters, useUserEdit, useUsersSorting, useUserRoles } from '@/hooks/users';
import { useVisibleColumns } from '@/hooks/table';

// Components
import { EditUserModal } from '@/components/users/EditUserModal';
import { UserRolesModal } from '@/components/users/UserRolesModal';
import { UserNotificationModal } from '@/components/users/UserNotificationModal';
import { UserCell } from '@/components/shared/UserCell';
import { RoleChip, RoleType } from '@/components/shared/RoleChip';
import { PermissionGuard } from '@/components/shared/PermissionGuard';
import { SearchInput } from '@/components/shared/SearchInput';
import { DateRangeFilter } from '@/components/shared/DateRangeFilter';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';

export default function UsersPage() {
  const { data, isLoading, isError, error } = useAtomValue(usersQueryAtom);
  const [params, setParams] = useAtom(usersListParamsAtom);

  // Custom hooks
  const {
    searchTerm,
    setSearchTerm,
    roleFilter,
    verifiedFilter,
    dateFilter,
    hasAnyFilter,
    handleSearch,
    handleClearSearch,
    clearFilters,
    handleRoleChange,
    handleVerifiedChange,
    handleDateChange,
    handlePageChange
  } = useUsersFilters();

  const { isEditOpen, editForm, setEditForm, isSaving, openEdit, saveEdit, onEditOpenChange } = useUserEdit();
  const { isRolesModalOpen, selectedUser, openRolesModal, onRolesModalOpenChange } = useUserRoles();
  const [isNotificationModalOpen, setNotificationModalOpen] = React.useState(false);
  const [notificationTarget, setNotificationTarget] = React.useState<UserListItem | null>(null);

  const users = React.useMemo<UserListItem[]>(() => data?.data ?? [], [data]);
  const { sortDescriptor, setSortDescriptor, sortedUsers } = useUsersSorting(users);

  // Function to trigger data refresh
  const triggerRefresh = React.useCallback(() => {
    setParams((prev) => ({ ...prev, refresh: Date.now() }));
  }, [setParams]);

  // Enhanced save function that triggers refresh
  const handleSaveEdit = React.useCallback(async () => {
    await saveEdit();
    // Trigger refresh after successful edit
    triggerRefresh();
  }, [saveEdit, triggerRefresh]);

  const notificationTargetUser = React.useMemo(() => {
    if (!notificationTarget) {
      return null;
    }

    return {
      id: notificationTarget.id,
      first_name: notificationTarget.first_name,
      last_name: notificationTarget.last_name,
      email: notificationTarget.email
    };
  }, [notificationTarget]);

  const openNotificationModal = React.useCallback((user: UserListItem) => {
    setNotificationTarget(user);
    setNotificationModalOpen(true);
  }, []);

  const handleNotificationModalChange = React.useCallback((open: boolean) => {
    setNotificationModalOpen(open);
    if (!open) {
      setNotificationTarget(null);
    }
  }, []);

  const renderCell = React.useCallback(
    (user: UserListItem, columnKey: React.Key) => {
      switch (columnKey) {
        case 'name':
          return <UserCell user={user} />;
        case 'verified': {
          const verified = Boolean(user.email_verified_at);

          return (
            <Chip color={verified ? 'success' : 'warning'} size="sm" variant="flat">
              {verified ? 'Verified' : 'Pending'}
            </Chip>
          );
        }
        case 'organizations': {
          if (!user.organizations || user.organizations.length === 0) {
            return (
              <div className="text-default-400 flex items-center gap-2">
                <RiBuildingLine className="h-4 w-4" />
                <span className="text-sm">No organizations</span>
              </div>
            );
          }

          return (
            <Popover placement="bottom-start" showArrow backdrop="opaque">
              <PopoverTrigger>
                <div className="hover:bg-default-100 flex cursor-pointer items-center gap-2 rounded-lg p-1 transition-colors">
                  <AvatarGroup max={3} size="sm">
                    {user.organizations.map((org) => (
                      <Avatar key={org.id} name={org.name} color="primary" size="sm" />
                    ))}
                  </AvatarGroup>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">
                      {user.organizations.length} workspace{user.organizations.length > 1 ? 's' : ''}
                    </span>
                    <span className="text-default-500 text-xs">Click to view details</span>
                  </div>
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-80">
                <div className="px-1 py-2">
                  <div className="text-small mb-3 flex items-center gap-2 font-bold">
                    <RiBuildingLine className="text-primary h-4 w-4" />
                    Workspaces
                  </div>
                  <div className="text-default-500 mb-3 text-xs">
                    Workspaces for {`${user.first_name} ${user.last_name}`.trim() || user.email}
                  </div>
                  <div className="flex flex-col gap-3">
                    {user.organizations.map((org) => (
                      <div
                        key={org.id}
                        className="hover:bg-default-50 flex items-center justify-between rounded-lg p-2 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="shrink-0">
                            <Avatar name={org.name} color="primary" size="sm" />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">{org.name}</span>
                            {org.desc && <span className="text-default-500 line-clamp-2 text-xs">{org.desc}</span>}
                          </div>
                        </div>
                        {org.role && <RoleChip role={org.role as RoleType} size="sm" variant="flat" />}
                      </div>
                    ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          );
        }
        case 'roles': {
          if (!user.roles || user.roles.length === 0) {
            return (
              <div className="text-default-400 flex items-center">
                <RiUserSettingsLine className="size-4" />
                <span className="ml-3 text-sm">No roles</span>
              </div>
            );
          }

          return (
            <div className="flex items-center gap-1">
              <PermissionGuard permission="User.assignRole">
                <Tooltip content="Manage roles">
                  <Button isIconOnly size="sm" variant="light" onPress={() => openRolesModal(user)}>
                    <RiUserSettingsLine className="size-4" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
              <div className="flex items-center gap-2">
                <div className="flex flex-wrap gap-1">
                  {user.roles.map((role) => {
                    // User roles: admin, super_admin, customer
                    const getRoleColor = (roleName: string) => {
                      const name = roleName.toLowerCase();
                      if (name === 'super_admin') return 'danger';
                      if (name === 'admin' || name.startsWith('admin')) return 'primary';
                      if (name === 'customer') return 'default';
                      return 'default';
                    };

                    const getRoleLabel = (roleName: string) => {
                      switch (roleName) {
                        case 'super_admin':
                          return 'Super Admin';
                        case 'admin':
                          return 'Admin';
                        case 'customer':
                          return 'Customer';
                        default:
                          return roleName;
                      }
                    };

                    return (
                      <Chip key={role.id} color={getRoleColor(role.name)} size="sm" variant="flat">
                        {getRoleLabel(role.name)}
                      </Chip>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        }
        case 'actions':
          return (
            <div className="relative flex items-center justify-end gap-2">
              <PermissionGuard permission="Notification.send">
                <Tooltip content="Send notification">
                  <Button isIconOnly size="sm" variant="light" onPress={() => openNotificationModal(user)}>
                    <RiNotificationLine className="h-5 w-5" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
              <PermissionGuard permission="User.update">
                <Tooltip content="Edit user">
                  <Button isIconOnly size="sm" variant="light" onPress={() => openEdit(user)}>
                    <RiEdit2Line className="h-5 w-5" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
            </div>
          );
        case 'created_at':
          return <DateTimeDisplay dateString={user.created_at} />;
        default:
          return null;
      }
    },
    [openEdit, openRolesModal, openNotificationModal]
  );

  const columns = React.useMemo(
    () => [
      { name: 'Name', uid: 'name', sortable: true },
      { name: 'Verified', uid: 'verified', sortable: true },
      { name: 'Workspaces', uid: 'organizations', sortable: true },
      { name: 'Roles', uid: 'roles', sortable: true },
      { name: 'Created At', uid: 'created_at', sortable: true },
      { name: 'Actions', uid: 'actions', align: 'end', cellClass: 'text-right' }
    ],
    []
  );

  // Columns visibility management
  const { visibleColumns, setVisibleColumns, headerColumns } = useVisibleColumns(columns);

  // Filter options
  const roleOptions = [
    { key: 'all', label: 'All' },
    { key: 'admin', label: 'Admin' },
    { key: 'super_admin', label: 'Super Admin' },
    { key: 'customer', label: 'Customer' }
  ];

  const verifiedOptions = [
    { key: 'all', label: 'All' },
    { key: 'verified', label: 'Verified' },
    { key: 'pending', label: 'Pending' }
  ];

  const visibleColumnsSet = React.useMemo(
    () => new Set(Array.from(visibleColumns).map((c) => c.toString())),
    [visibleColumns]
  );

  const usersMeta = data?.meta;

  const {
    exportState,
    isExporting,
    needsConfirmation,
    totalRecords,
    handleRequestExport,
    handleConfirmExport,
    handleCancelExport
  } = useTableExport<UserListItem>({
    resourceName: 'Users',
    apiPath: '/users',
    params: {
      page: params.page,
      per_page: params.per_page,
      filters: params.filters,
      sort: undefined
    },
    meta: usersMeta
      ? {
          total: usersMeta.total,
          per_page: usersMeta.per_page,
          current_page: usersMeta.current_page,
          last_page: usersMeta.last_page
        }
      : undefined,
    columns: columns.map((c) => ({ name: c.name, uid: c.uid })),
    visibleColumns: visibleColumnsSet,
    hasAnyFilter,
    mapItemToRow: (user) => ({
      name: `${user.first_name} ${user.last_name}`.trim() || user.email,
      verified: user.email_verified_at ? 'Verified' : 'Pending',
      organizations: user.organizations?.map((o) => o.name).join(' | ') ?? '',
      roles: user.roles?.map((r) => r.name).join(', ') ?? '',
      created_at: user.created_at
    })
  });

  return (
    <TablePage
      columns={columns}
      currentPage={data?.meta?.current_page}
      data={sortedUsers}
      emptyMessage="No users found"
      error={error}
      errorMessage="Failed to load users"
      filtersSection={
        <TableFilters
          hasActiveFilters={hasAnyFilter}
          isRefreshing={isLoading}
          onClearFilters={clearFilters}
          onRefresh={triggerRefresh}
          rightSection={
            <Button
              color="success"
              size="sm"
              variant="flat"
              onPress={handleRequestExport}
              isLoading={isExporting}
              isDisabled={isLoading || !data?.meta?.total}
              startContent={<RiDownloadLine className="h-4 w-4" />}
            >
              Export CSV
            </Button>
          }
        >
          <SearchInput
            placeholder="Search users..."
            value={searchTerm}
            onValueChange={setSearchTerm}
            onSearch={handleSearch}
            onClear={handleClearSearch}
          />

          <ColumnsToggleDropdown
            allColumns={columns.map((c) => ({ uid: c.uid, name: c.name }))}
            selected={visibleColumns}
            onChange={(next) => setVisibleColumns(next)}
          />

          <FilterSelect label="Role" options={roleOptions} value={roleFilter} onChange={handleRoleChange} />

          <FilterSelect
            label="Verified"
            options={verifiedOptions}
            value={verifiedFilter}
            onChange={handleVerifiedChange}
          />

          <DateRangeFilter label="Created Date" value={dateFilter} onChange={handleDateChange} />
        </TableFilters>
      }
      headerColumns={headerColumns}
      isError={isError}
      isLoading={isLoading}
      renderCell={renderCell}
      sortDescriptor={sortDescriptor}
      title="Users"
      totalPages={data?.meta?.last_page}
      onPageChange={handlePageChange}
      onRefresh={triggerRefresh}
      onSortChange={(d) => setSortDescriptor(d)}
    >
      {needsConfirmation && (
        <ExportConfirmModal
          isOpen={exportState === 'confirm'}
          onConfirm={handleConfirmExport}
          onCancel={handleCancelExport}
          totalRecords={totalRecords}
          resourceLabel="user"
        />
      )}
      <EditUserModal
        editForm={editForm}
        isOpen={isEditOpen}
        isSaving={isSaving}
        setEditForm={setEditForm}
        onOpenChange={onEditOpenChange}
        onSave={handleSaveEdit}
      />

      <UserRolesModal
        isOpen={isRolesModalOpen}
        onOpenChange={onRolesModalOpenChange}
        user={selectedUser}
        onSuccess={triggerRefresh}
      />

      <UserNotificationModal
        isOpen={isNotificationModalOpen}
        onOpenChange={handleNotificationModalChange}
        targetUser={notificationTargetUser}
        showRecipientSelector={false}
      />
    </TablePage>
  );
}
